import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
    Injectable, inject
} from "@angular/core";
import {
    AutocompleteRequest,
    BulkResponse,
    BulkUtil,
    CreateRequest,
    DeleteRequest,
    DeleteResponse,
    GetRequest,
    Product,
    ProductSearchFilterOptions,
    RequestUtil,
    SearchResponse,
    UpdateRequest
} from "@shopliftr/common-js/shared";
import { DealsApiProductSearchSubmission } from "@shopliftr/common-js/admin";
import {
    ISearchSort, ModelAction
} from "@shopliftr/common-js/data";
import {
    IFullAccessInternalApiClientService, ListUtil
} from "@shopliftr/common-ng";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";

/**
 * API Client Service for Product entities.
 *
 * Implements full CRUD + autocomplete + deals-api search for Product via v2 endpoints.
 * Write operations (create/update/delete) delegate to InternalApiRequestService.
 * For admin bulk searches, use dealsApiSearch$. For the lean smart-app read path, use search$.
 *
 * @pattern IFullAccessInternalApiClientService
 * @implements {IFullAccessInternalApiClientService<Product>}
 * @providedIn root
 */
@Injectable({ providedIn: "root" })
export class ProductApiClientService implements IFullAccessInternalApiClientService<Product> {

    readonly basePath = "product";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    getBrands$(text: string): Observable<Array<string>> {
        const request = new AutocompleteRequest();
        request.subject = Product.modelName;
        request.field = "brand";
        request.text = text;
        return this._internalApiRequestService.autocomplete$(request, this.basePath).pipe(
            map((response) => response.results)
        );
    }

    getManufacturers$(text: string): Observable<Array<string>> {
        const request = new AutocompleteRequest();
        request.subject = Product.modelName;
        request.field = "manufacturer";
        request.text = text;
        return this._internalApiRequestService.autocomplete$(request, this.basePath).pipe(
            map((response) => response.results)
        );
    }

    /**
     * Retrieves all products matching the given filter criteria.
     *
     * `filterOptions` is passed directly as the `options` argument to `RequestUtil.createSearchRequest`
     * (it is the model registered via `@requestOptions` for `Product` `SearchRequest`s).
     *
     * @param filterOptions - Filter criteria
     * @param sort - Optional sort configuration
     * @returns Observable of all matching products
     */
    getAll$(filterOptions: ProductSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<Product>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Product.modelName,
            undefined,
            undefined,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.getAll$<Product, ProductSearchFilterOptions>(
            request, this.basePath
        );
    }

    /**
     * Performs a paginated v2 product search using the standard SearchRequest envelope.
     *
     * Delegates to `InternalApiRequestService.search$()` which POSTs to `/v2/product`.
     * Use `dealsApiSearch$()` for searches requiring the full 17-field filter shape.
     *
     * `filterOptions` is passed directly as the `options` argument to `RequestUtil.createSearchRequest`
     * (it is the model registered via `@requestOptions` for `Product` `SearchRequest`s).
     *
     * @param filterOptions - Filter criteria for the search
     * @param page - Zero-based page number
     * @param pageSize - Results per page
     * @param sort - Optional sort configuration
     * @returns Observable of `SearchResponse<Product>`
     */
    search$(
        filterOptions: ProductSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<Product>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Product.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.search$<Product, ProductSearchFilterOptions>(
            request, this.basePath
        );
    }

    /**
     * Performs a paginated product search via the Deals API search endpoint.
     *
     * Submits a `DealsApiProductSearchSubmission` body to `/v2/product/deals-api-search`
     * via `InternalApiRequestService.submit$()` and maps the response payload.
     * Use this for admin searches requiring the full 17-field `DealsAPIProductSearchFilters` shape.
     *
     * @param options - Deals-API search options including pagination, sort, and full filter
     * @returns Observable of `SearchResponse<Product>`
     */
    dealsApiSearch$(options: DealsApiProductSearchSubmission): Observable<SearchResponse<Product>> {
        const request = RequestUtil.createSubmitRequest(options);
        return this._internalApiRequestService.submit$<DealsApiProductSearchSubmission, SearchResponse<Product>>(
            request, `${this.basePath}/deals-api-search`
        ).pipe(map((response) => response.payload));
    }

    get$(upc: string): Observable<Product> {
        const getRequest: GetRequest<Product> = RequestUtil.createGetRequest(
            upc, Product.modelName, ModelAction.ClientGet
        );
        return this._internalApiRequestService.get$(getRequest, this.basePath).pipe(
            map((response) => response.payload as Product)
        );
    }

    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id) => {
            const request = RequestUtil.createGetRequest<Product>(id, Product.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    create$(fields: Product): Observable<Product> {
        const createRequest = RequestUtil.createCreateRequest<Product, void>(
            ModelAction.ClientCreate,
            fields.cloneForAction(ModelAction.ClientCreate)
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath)
            .pipe(map((response) => response.payload));
    }

    bulkCreate$(items: Array<CreateRequest<Product, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    update$(id: string, version: number, fields: Product, removeFields?: Array<string>): Observable<Product> {
        const updateFields = fields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<Product> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, updateFields, version, removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath)
            .pipe(map((response) => response.payload));
    }

    updateWithChanges$(original: Product, updated: Product): Observable<Product> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    delete$(id: string, version: number): Observable<DeleteResponse> {
        const deleteRequest = RequestUtil.createDeleteRequest<Product>(
            id, Product.modelName, ModelAction.ClientDelete, version
        );
        return this._internalApiRequestService.delete$(deleteRequest, this.basePath);
    }

    bulkDelete$(items: Array<DeleteRequest<unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Returns the total number of `Product` catalog records matching the given UPCs.
     *
     * Mirrors the `ProductSearchFilterOptions.upcs`-scoped query shape already used by
     * `ProductListComponent`'s `presetUpcs`-driven Duplicate Products tab (see
     * `ngOnInit`'s `presetUpcs` branch), so callers that only have a UPC list (e.g. a
     * live duplicate-product-count fetch) don't need to build filter options themselves.
     * A narrow, purpose-specific signature was chosen over accepting a pre-built
     * `ProductSearchFilterOptions` to mirror `DataEntryDealApiClientService.getCount$`/
     * `dealExistsForUpc$`, which all build their filter options internally
     * from primitive arguments rather than taking the filter options as a parameter.
     *
     * @param upcs - The UPCs to count matching products for
     * @returns Observable of the total matching product count
     */
    getCountByUpcs$(upcs: Array<string>): Observable<number> {
        const filterOptions = new ProductSearchFilterOptions();
        filterOptions.upcs = [...(upcs ?? [])];
        return this.search$(filterOptions, 0, 0).pipe(map((value) => value.total));
    }
}
